import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { resolveHref } from './cellLinks';

const linkSx: SxProps<Theme> = {
  color: 'primary.main',
  fontWeight: 600,
  textDecoration: 'none',
  borderBottom: '1px solid',
  borderColor: (t: Theme) => alpha(t.palette.primary.main, 0.4),
  '&:hover': { borderColor: 'primary.main' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
};

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/**
 * One link in an assistant answer.
 *
 * Internal paths become `RouterLink`s so review stays in the SPA — and so the router
 * basename is applied; a raw `<a href="/applications/…">` would drop `/admin`.
 * External http(s) links open in a new tab with the announcement a screen reader
 * needs. Anything else is not a link at all: the label is kept, the anchor is not.
 */
export function ChatLink({ href, children }: { href: string; children: ReactNode }) {
  const resolved = resolveHref(href);
  if (!resolved) return <>{children}</>;

  if (resolved.kind === 'internal') {
    return (
      <Box component={RouterLink} to={resolved.path} sx={linkSx}>
        {children}
      </Box>
    );
  }

  return (
    <Box component="a" href={resolved.url} target="_blank" rel="noopener noreferrer" sx={linkSx}>
      {children}
      <Box component="span" sx={visuallyHiddenSx}>
        {' '}
        (opens in a new tab)
      </Box>
    </Box>
  );
}
